/**
 * CSRF PROTECTION SERVICE
 * 
 * SECURITY IMPLEMENTATION:
 * - Manages CSRF tokens for state-changing requests
 * - Uses double-submit cookie pattern
 * - Automatically includes CSRF headers in API requests
 * - Prevents Cross-Site Request Forgery attacks
 */

import { secureLogger } from '@/utils/secureLogger';

class CSRFService {
  private csrfToken: string | null = null;

  /**
   * Get CSRF token from cookie (set by server)
   */
  private getCSRFTokenFromCookie(): string | null {
    const cookieMatch = document.cookie.match(/csrf-token=([^;]+)/);
    return cookieMatch ? cookieMatch[1] : null;
  }

  /**
   * Get current CSRF token (from cookie first, then cached)
   */
  public getCSRFToken(): string | null {
    // Try to get from cookie first (most up-to-date)
    const cookieToken = this.getCSRFTokenFromCookie();
    if (cookieToken) {
      this.csrfToken = cookieToken;
      return cookieToken;
    }

    // Fallback to cached token
    return this.csrfToken;
  }

  /**
   * Fetch fresh CSRF token from server
   */
  public async fetchCSRFToken(): Promise<string | null> {
    try {
      const rawBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const apiOrigin = rawBase.replace(/\/api\/v1\/?$/, '');
      const url = `${apiOrigin}/api/v1/auth/csrf-token`;
      
      // Firefox-specific: Ensure proper fetch configuration
      // Firefox is stricter with CORS and requires explicit CORS mode
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Include cookies (required for CSRF cookie)
        mode: 'cors', // Explicitly set CORS mode for Firefox compatibility
        cache: 'no-store', // Firefox can cache aggressively - prevent caching
        headers: {
          'Accept': 'application/json',
          // Don't include Content-Type for GET requests in Firefox (can trigger preflight)
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.csrf_token) {
          this.csrfToken = data.data.csrf_token;
          secureLogger.debug('🛡️ CSRF token fetched successfully');
          return this.csrfToken;
        }
      }

      // Only log if it's not a network error (likely backend not ready yet)
      if (response.status !== 0) {
        secureLogger.warn('❌ Failed to fetch CSRF token:', response.status);
      }
      return null;
    } catch (error: any) {
      // Firefox reports NetworkError differently - check for both patterns
      const isNetworkError = 
        error instanceof TypeError || 
        error.name === 'TypeError' ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('network error');
      
      // Only log non-network errors (actual issues) or in development for debugging
      if (import.meta.env.DEV) {
        if (!isNetworkError) {
          secureLogger.warn('⚠️ CSRF token fetch error:', error.message || error);
        }
        // Silently handle network errors in Firefox - they're often due to timing/CORS during initialization
      }
      return null;
    }
  }

  /**
   * Ensure we have a valid CSRF token
   */
  public async ensureCSRFToken(): Promise<string | null> {
    let token = this.getCSRFToken();
    
    if (!token) {
      token = await this.fetchCSRFToken();
    }

    return token;
  }

  /**
   * Get CSRF headers for API requests
   */
  public async getCSRFHeaders(): Promise<Record<string, string>> {
    let token = await this.ensureCSRFToken();
    
    if (!token) {
      secureLogger.warn('⚠️ No CSRF token available, attempting to fetch fresh token');
      // Force fetch a fresh token
      token = await this.fetchCSRFToken();
    }
    
    if (!token) {
      secureLogger.error('❌ Failed to obtain CSRF token');
      return {};
    }

    return {
      'X-CSRF-Token': token,
    };
  }

  /**
   * Refresh CSRF token (useful when current token is invalid)
   */
  public async refreshCSRFToken(): Promise<string | null> {
    secureLogger.debug('🔄 Refreshing CSRF token...');
    this.clearCSRFToken();
    return await this.fetchCSRFToken();
  }

  /**
   * Clear cached CSRF token (on logout, etc.)
   */
  public clearCSRFToken(): void {
    this.csrfToken = null;
    secureLogger.debug('🧹 CSRF token cleared');
  }

  /**
   * Initialize CSRF protection (call on app startup)
   */
  public async initialize(): Promise<void> {
    try {
      const token = await this.ensureCSRFToken();
      if (token) {
        secureLogger.debug('🛡️ CSRF protection initialized');
      } else {
        // Silent fail - backend may not be ready yet, will retry on first API call
        // Firefox often throws NetworkError during initialization, which is expected
        // The token will be fetched on-demand when needed
        if (import.meta.env.DEV) {
          // Use debug level for Firefox initialization issues (too noisy otherwise)
          secureLogger.debug('⚠️ CSRF token not available yet (will retry on first API call)');
        }
      }
    } catch (error: any) {
      // Firefox-specific: Check for various network error patterns
      const isNetworkError = 
        error instanceof TypeError || 
        error.name === 'TypeError' ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('network error');
      
      // Only log actual errors, not network issues during initialization (common in Firefox)
      if (!isNetworkError) {
        secureLogger.error('❌ CSRF initialization failed:', error);
      }
    }
  }
}

// Export singleton instance
export const csrfService = new CSRFService();
export default csrfService;